import NeptuneElement from '../../NeptuneElement.js';
import './stepperItem/StepperItem.js';

class Stepper extends NeptuneElement {
    static ORIENTATIONS = ['horizontal', 'vertical'];

    templateMarkup = `
        <ol>
            <slot></slot>
        </ol>
    `;
    componentCssUrl = '/src/components/core/stepper/stepper.css';

    #steps = [];
    #activeStep = 0;
    #handleStepClickBound = null;

    constructor() {
        super();
    }

    static get observedAttributes() {
        return ['active-step', 'orientation'];
    }

    async connectedCallback() {
        await super.connectedCallback();
        this.#collectSteps();
        this.setEventListeners();
        this.#activeStep = Number(this.getAttribute('active-step')) || 0;
        this.#updateSteps();
        this.processInitialAttributesAfterRender(Stepper.observedAttributes);
    }

    async disconnectedCallback() {
        await super.disconnectedCallback();
    }

    attributeChangedCallback(name, oldValue, newValue) {
        if (!this.isRendered) {return;}
        this.processAttribute(name, oldValue, newValue);
    }

    processAttribute(name, oldValue, newValue) {
        if (oldValue === newValue) {return;}
        switch (name) {
            case 'active-step':
                this.#setActiveStep(Number(newValue));
                break;
            case 'orientation':
                this.validatePropertyValue(name, newValue, Stepper.ORIENTATIONS);
                this.#applyOrientation(newValue);
                break;
            default:
                break;
        }
    }

    setEventListeners() {
        this.#handleStepClickBound = this.#handleStepClick.bind(this);
        this.addEventListener('click', this.#handleStepClickBound);
    }

    removeEventListeners() {
        this.removeEventListener('click', this.#handleStepClickBound);
        this.#handleStepClickBound = null;
        this.#steps = [];
    }

    goToStep(index) {
        if (this.#steps.length === 0) {
            this.#collectSteps();
        }
        if (index < 0 || index >= this.#steps.length) {return;}
        this.#activeStep = index;
        this.setAttribute('active-step', index);
        this.#updateSteps();
        this.dispatchEvent(new CustomEvent('step-changed', {
            detail: { currentStep: index },
            bubbles: true,
            composed: true
        }));
    }

    #collectSteps() {
        this.#steps = Array.from(this.querySelectorAll('neptune-stepper-item'));
        this.#steps.forEach((step, index) => {
            step.setAttribute('data-step', String(index + 1));
        });

        if (this.#steps.length > 3) {
            this.#steps.forEach(step => {
                step.setAttribute('data-multi-step', '');
            });
        }
    }

    #handleStepClick(event) {
        const stepItem = event.target.closest('neptune-stepper-item');
        if (!stepItem) {return;}
        const index = this.#steps.indexOf(stepItem);
        if (index === -1 || index > this.#activeStep) {return;}
        this.goToStep(index);
    }

    #setActiveStep(index) {
        if (typeof index !== 'number' || isNaN(index) || index < 0 || index > this.#steps.length) {return;}
        this.#activeStep = index;
        this.#updateSteps();
    }

    #applyOrientation(orientation) {
        const isVertical = orientation === 'vertical';
        this.#steps.forEach(step => {
            if (isVertical) {
                step.setAttribute('orientation-vertical', '');
            } else {
                step.removeAttribute('orientation-vertical');
            }
        });
    }

    #updateSteps() {
        if (!this.#steps.length) {return;}
        this.#steps.forEach((step, index) => {
            step.removeAttribute('active');
            step.removeAttribute('completed');
            if (this.#activeStep >= this.#steps.length) {
                step.setAttribute('completed', '');
            } else if (index < this.#activeStep) {
                step.setAttribute('completed', '');
            } else if (index === this.#activeStep && this.#activeStep < this.#steps.length) {
                step.setAttribute('active', '');
            }
        });
    }
}

customElements.define('neptune-stepper', Stepper);
export default Stepper;